import React from 'react';
import {
    Button,
    Progress,
    Row,
    Col,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import {
    faSave,
    faClock,
    faCheckCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    ServiceResponseResult,
} from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    FormGroupColumn,
    GroupedRow,
    onFieldChange,
    onReactSelectChanged,
    ToastMessage,
    FormCheckbox,
    Input,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';

class OperationalCategory {
    id = null;
    name = '';
    isActive = true;
    showOnDispatchPlus = false;
    excludeFromDispatchPlusJobCount = false;
    usePreJobForm = false;
}

class OperationalCategoryForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            originalData: new OperationalCategory(),
            operationalCategory: new OperationalCategory(),
            loading: true,
            saving: false,
        };

        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps &&
            this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        const cloned = clone(this.state.operationalCategory);

        if (this.props.location.pathname == AppNavPaths.OperationalCategoryNew) {
            await this.create(cloned);
        } else {
            await this.update(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (operationalCategory) => {
        operationalCategory.id = 0;

        const response = await httpClient
            .post(ApiRoutes.operationalCategories.create(), operationalCategory)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Saved."
                    />,
                );

                this.context.history.push(
                    `${AppNavPaths.OperationalCategory}/${response.data}`,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        change: onFieldChange,
        select: onReactSelectChanged,
    };

    async populateState() {
        const { id } = this.props.match.params;
        const operationalCategory = id
            ? await httpClient.js(ApiRoutes.operationalCategories.getById(id))
            : new OperationalCategory();

        this.setState({
            originalData: { ...operationalCategory },
            operationalCategory: {
                ...operationalCategory,
            },
            loading: false,
            saving: false,
        });
    }

    onCheckedChanged = (fieldName) => (e) => {
        const { operationalCategory } = this.state;

        operationalCategory[fieldName] = e.target.checked;
        this.setState({ operationalCategory });
    };

    update = async (operationalCategory) => {
        const response = await httpClient
            .put(ApiRoutes.operationalCategories.update(operationalCategory.id), operationalCategory)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                this.setState({
                    saving: false,
                    formValidated: false,
                });

                this.populateState();

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Operational Category saved."
                    />,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    renderForm() {
        const {
            operationalCategory,
            originalData,
            saving,
            errors,
            loading,
            formValidated,
        } = this.state;

        if (!operationalCategory) {
            return '';
        }

        const newOperationalCategory = parseInt(operationalCategory.id ?? 0) <= 0;

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, operationalCategory)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="operationalCategoryForm"
                    formHeadingIcon={faClock}
                    formHeading={
                        newOperationalCategory
                            ? 'New Operational Category'
                            : 'Edit Operational Category'
                    }
                    formName="operationalCategoryForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    backUrl={AppNavPaths.OperationalCategories}
                    backLabel="Back to Operational Categories"
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="Operational Category Name"
                                id="name"
                                name="operationalCategory.name"
                                value={operationalCategory.name ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Name"
                                error="Name is required"
                                required
                            />
                            <Row>
                                <Col>
                                    <FormCheckbox
                                        className="mt-2 mb-2"
                                        id="chkOperationalCategoryIsActive"
                                        checked={operationalCategory.isActive}
                                        onChange={this.onCheckedChanged('isActive')}
                                        labelText="Active"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <FormCheckbox
                                        className="mt-2 mb-2"
                                        id="chkShowOnDispatchPlus"
                                        checked={operationalCategory.showOnDispatchPlus}
                                        onChange={this.onCheckedChanged('showOnDispatchPlus')}
                                        labelText="Show on Dispatch Plus"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <FormCheckbox
                                        className="mt-2 mb-2"
                                        id="chkExcludeFromDispatchPlusJobCount"
                                        checked={operationalCategory.excludeFromDispatchPlusJobCount}
                                        onChange={this.onCheckedChanged('excludeFromDispatchPlusJobCount')}
                                        labelText="Exclude from Dispatch Plus Job Count"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <FormCheckbox
                                        className="mt-2 mb-2"
                                        id="chkUsePreJobForm"
                                        checked={operationalCategory.usePreJobForm}
                                        onChange={this.onCheckedChanged('usePreJobForm')}
                                        labelText="Use Pre-Job Form"
                                    />
                                </Col>
                            </Row>

                        </FormGroupColumn>
                    </GroupedRow>

                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="operationalCategoryForm"
                        >
                            <FontAwesomeIcon
                                className="mr-1"
                                icon={faSave}
                            />
                            {newOperationalCategory ? 'Save New Operational Category' : 'Save'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
            </>
        );
    }

    render() {
        const { loading } = this.state;

        if (loading) {
            return <Progress />;
        }

        return this.renderForm();
    }
}
export default OperationalCategoryForm;
